/**
 * Motion dice tuning: every number that sets how the 3D dice feel (physics, throw strength,
 * phone and swipe input, timing, what counts as a bad landing). The engine, `toss.ts`,
 * `rolling.ts` and `DiceTray` read `tuning()` at the moment they use a value, so a change applies
 * to the next throw.
 *
 * Admins change these in the Dice Lab (Admin tools → Dice Lab, `components/DiceLab.tsx`). Saved
 * values live in this browser only (`localStorage`, `kadice.diceTuning`) and apply to every roll
 * shown on this device; nobody else is affected (every screen simulates throws on its own and
 * the server's values still come up). Send Feedback attaches them to the report's snapshot
 * (`tuningSnapshot`).
 */

export interface DiceTuning {
  // Physics
  gravity: number;
  substeps: number;
  woodFriction: number;
  woodBounce: number;
  dieFriction: number;
  dieBounce: number;
  linearDamping: number;
  angularDamping: number;
  settleStart: number;
  settleRamp: number;
  settleDamping: number;
  easeFlat: number;
  sleepSpeed: number;
  sleepTime: number;
  wallBounce: number;
  // Throw
  minStrength: number;
  speedBase: number;
  speedLinear: number;
  speedCubic: number;
  sideways: number;
  liftBase: number;
  liftLinear: number;
  lobLift: number;
  lobForward: number;
  driveForward: number;
  driveLift: number;
  spinBase: number;
  spinLinear: number;
  spinCubic: number;
  speedJitter: number;
  driftJitter: number;
  spinScatter: number;
  spinJitter: number;
  botStrengthMin: number;
  botStrengthRange: number;
  // Input
  fullHandSpeed: number;
  stillHandSpeed: number;
  flickWindowMs: number;
  loftGain: number;
  sideHand: number;
  swingCredit: number;
  shakeSpin: number;
  fullSwipe: number;
  shakeRelease: number;
  shakeMinShakes: number;
  shakeAccel: number;
  shakeGapMs: number;
  shakeFlick: number;
  shakeStillMs: number;
  // Timing
  rollMs: number;
  quickMs: number;
  holdMs: number;
  rethrowMs: number;
  // Bad landing
  leanHeight: number;
  maxRethrows: number;
  rethrowPauseMs: number;
  myRethrowPauseMs: number;
}

export const DEFAULT_TUNING: Readonly<DiceTuning> = {
  gravity: 42,
  substeps: 3,
  woodFriction: 0.28,
  woodBounce: 0.38,
  dieFriction: 0.18,
  dieBounce: 0.45,
  linearDamping: 0.08,
  angularDamping: 0.08,
  settleStart: 0.55,
  settleRamp: 0.3,
  settleDamping: 0.9,
  easeFlat: 0.15,
  sleepSpeed: 0.25,
  sleepTime: 0.25,
  wallBounce: 0.4,
  minStrength: 0.3,
  speedBase: 6,
  speedLinear: 24,
  speedCubic: 60,
  sideways: 1,
  liftBase: 3,
  liftLinear: 7,
  lobLift: 1,
  lobForward: 0.4,
  driveForward: 0.3,
  driveLift: 0.6,
  spinBase: 8,
  spinLinear: 24,
  spinCubic: 40,
  speedJitter: 0.3,
  driftJitter: 2,
  spinScatter: 0.6,
  spinJitter: 6,
  botStrengthMin: 0.35,
  botStrengthRange: 0.5,
  fullHandSpeed: 5,
  stillHandSpeed: 0.25,
  flickWindowMs: 450,
  loftGain: 1.5,
  sideHand: 1.6,
  swingCredit: 1,
  shakeSpin: 0.65,
  fullSwipe: 4.5,
  shakeRelease: 0,
  shakeMinShakes: 2,
  shakeAccel: 12,
  shakeGapMs: 700,
  shakeFlick: 1.5,
  shakeStillMs: 250,
  rollMs: 3200,
  quickMs: 2200,
  holdMs: 650,
  rethrowMs: 1700,
  leanHeight: 1.25,
  maxRethrows: 5,
  rethrowPauseMs: 1600,
  myRethrowPauseMs: 900,
};

export type TuningKey = keyof DiceTuning;
export type TuningGroup = 'physics' | 'throw' | 'input' | 'timing' | 'bad';

/**
 * Each value's slider: its group, range and step. Labels and hints: `diceLab.f.<key>` /
 * `diceLab.h.<key>` in text.json. `choice`: a row of buttons instead, one per value from `min`
 * to `max` (`diceLab.v.<key>.<value>`).
 */
export const TUNING_FIELDS: readonly {
  key: TuningKey;
  group: TuningGroup;
  min: number;
  max: number;
  step: number;
  choice?: boolean;
}[] = [
  { key: 'gravity', group: 'physics', min: 5, max: 120, step: 1 },
  { key: 'substeps', group: 'physics', min: 1, max: 8, step: 1 },
  { key: 'woodFriction', group: 'physics', min: 0, max: 1, step: 0.01 },
  { key: 'woodBounce', group: 'physics', min: 0, max: 1, step: 0.01 },
  { key: 'dieFriction', group: 'physics', min: 0, max: 1, step: 0.01 },
  { key: 'dieBounce', group: 'physics', min: 0, max: 1, step: 0.01 },
  { key: 'linearDamping', group: 'physics', min: 0, max: 0.9, step: 0.01 },
  { key: 'angularDamping', group: 'physics', min: 0, max: 0.9, step: 0.01 },
  { key: 'settleStart', group: 'physics', min: 0.1, max: 0.95, step: 0.01 },
  { key: 'settleRamp', group: 'physics', min: 0.05, max: 0.9, step: 0.01 },
  { key: 'settleDamping', group: 'physics', min: 0, max: 0.99, step: 0.01 },
  { key: 'easeFlat', group: 'physics', min: 0.02, max: 0.5, step: 0.01 },
  { key: 'sleepSpeed', group: 'physics', min: 0.01, max: 2, step: 0.01 },
  { key: 'sleepTime', group: 'physics', min: 0.05, max: 2, step: 0.05 },
  { key: 'wallBounce', group: 'physics', min: 0, max: 1, step: 0.01 },
  { key: 'minStrength', group: 'throw', min: 0, max: 0.5, step: 0.01 },
  { key: 'speedBase', group: 'throw', min: 0, max: 30, step: 0.5 },
  { key: 'speedLinear', group: 'throw', min: 0, max: 80, step: 1 },
  { key: 'speedCubic', group: 'throw', min: 0, max: 150, step: 1 },
  { key: 'sideways', group: 'throw', min: 0, max: 1.5, step: 0.05 },
  { key: 'liftBase', group: 'throw', min: 0, max: 20, step: 0.5 },
  { key: 'liftLinear', group: 'throw', min: 0, max: 30, step: 0.5 },
  { key: 'lobLift', group: 'throw', min: 0, max: 3, step: 0.05 },
  { key: 'lobForward', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'driveForward', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'driveLift', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'spinBase', group: 'throw', min: 0, max: 40, step: 0.5 },
  { key: 'spinLinear', group: 'throw', min: 0, max: 80, step: 1 },
  { key: 'spinCubic', group: 'throw', min: 0, max: 120, step: 1 },
  { key: 'speedJitter', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'driftJitter', group: 'throw', min: 0, max: 10, step: 0.1 },
  { key: 'spinScatter', group: 'throw', min: 0, max: 1.5, step: 0.05 },
  { key: 'spinJitter', group: 'throw', min: 0, max: 30, step: 0.5 },
  { key: 'botStrengthMin', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'botStrengthRange', group: 'throw', min: 0, max: 1, step: 0.01 },
  { key: 'fullHandSpeed', group: 'input', min: 0.5, max: 15, step: 0.1 },
  { key: 'stillHandSpeed', group: 'input', min: 0, max: 2, step: 0.05 },
  { key: 'flickWindowMs', group: 'input', min: 100, max: 1500, step: 10 },
  { key: 'loftGain', group: 'input', min: 0, max: 4, step: 0.05 },
  { key: 'sideHand', group: 'input', min: 0.5, max: 4, step: 0.05 },
  { key: 'swingCredit', group: 'input', min: 0, max: 1.5, step: 0.05 },
  { key: 'shakeSpin', group: 'input', min: 0, max: 1, step: 0.01 },
  { key: 'fullSwipe', group: 'input', min: 0.5, max: 15, step: 0.1 },
  { key: 'shakeRelease', group: 'input', min: 0, max: 1, step: 1, choice: true },
  { key: 'shakeMinShakes', group: 'input', min: 1, max: 6, step: 1 },
  { key: 'shakeAccel', group: 'input', min: 4, max: 40, step: 0.5 },
  { key: 'shakeGapMs', group: 'input', min: 200, max: 2000, step: 50 },
  { key: 'shakeFlick', group: 'input', min: 1, max: 4, step: 0.05 },
  { key: 'shakeStillMs', group: 'input', min: 80, max: 1500, step: 10 },
  { key: 'rollMs', group: 'timing', min: 1500, max: 8000, step: 100 },
  { key: 'quickMs', group: 'timing', min: 1200, max: 8000, step: 100 },
  { key: 'holdMs', group: 'timing', min: 0, max: 3000, step: 50 },
  { key: 'rethrowMs', group: 'timing', min: 600, max: 5000, step: 100 },
  { key: 'leanHeight', group: 'bad', min: 0.5, max: 1.6, step: 0.01 },
  { key: 'maxRethrows', group: 'bad', min: 0, max: 10, step: 1 },
  { key: 'rethrowPauseMs', group: 'bad', min: 0, max: 5000, step: 100 },
  { key: 'myRethrowPauseMs', group: 'bad', min: 0, max: 5000, step: 100 },
];

export const TUNING_GROUPS: readonly TuningGroup[] = ['physics', 'throw', 'input', 'timing', 'bad'];

const STORE_KEY = 'kadice.diceTuning';

/** Saved values over the defaults; unknown or broken entries are ignored. */
function load(): DiceTuning {
  const out = { ...DEFAULT_TUNING };
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null') as Record<string, unknown> | null;
    if (raw && typeof raw === 'object') {
      for (const f of TUNING_FIELDS) {
        const v = raw[f.key];
        if (typeof v === 'number' && Number.isFinite(v)) out[f.key] = Math.min(f.max, Math.max(f.min, v));
      }
    }
  } catch {
    // Storage blocked or broken: the defaults.
  }
  return out;
}

let saved = load();
let current: DiceTuning = { ...saved };

/** The values in use right now (the Dice Lab's unsaved edits included). */
export const tuning = (): Readonly<DiceTuning> => current;
/** The values saved on this device. */
export const savedTuning = (): Readonly<DiceTuning> => saved;

/** Use these values from now on (not saved: `saveTuning`). */
export function setTuning(next: DiceTuning): void {
  current = { ...next };
}

/** Save the values in use on this device. Returns false if storage is blocked. */
export function saveTuning(): boolean {
  saved = { ...current };
  try {
    const changed = changedFrom(saved);
    if (Object.keys(changed).length === 0) localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, JSON.stringify(changed));
    return true;
  } catch {
    return false;
  }
}

/** Back to the saved values (drops unsaved edits). */
export function revertTuning(): void {
  current = { ...saved };
}

/** The values that differ from the defaults. */
export function changedFrom(t: Readonly<DiceTuning>): Partial<DiceTuning> {
  const out: Partial<DiceTuning> = {};
  for (const f of TUNING_FIELDS) if (t[f.key] !== DEFAULT_TUNING[f.key]) out[f.key] = t[f.key];
  return out;
}

/** For Send Feedback's snapshot: what this device has saved, and anything unsaved in the Dice Lab. */
export function tuningSnapshot(): Record<string, unknown> {
  const unsaved = TUNING_FIELDS.some((f) => current[f.key] !== saved[f.key]);
  return {
    changedFromDefaults: changedFrom(saved),
    saved,
    ...(unsaved ? { unsavedInDiceLab: changedFrom(current) } : {}),
  };
}
